import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Checkbox,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  HourglassEmpty as HourglassIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import adminCommissionService from '../../services/adminCommissionService';

const STATUS_CONFIG = {
  draft: { color: 'default', icon: <ScheduleIcon fontSize="small" />, bg: '#f5f5f5', accent: '#9e9e9e' },
  pending: { color: 'warning', icon: <HourglassIcon fontSize="small" />, bg: '#fff8e1', accent: '#f57c00' },
  link_sent: { color: 'info', icon: <SendIcon fontSize="small" />, bg: '#e1f5fe', accent: '#0288d1' },
  payment_initiated: { color: 'secondary', icon: <PaymentIcon fontSize="small" />, bg: '#f3e5f5', accent: '#7b1fa2' },
  paid: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, bg: '#e8f5e9', accent: '#2e7d32' },
  overdue: { color: 'error', icon: <WarningIcon fontSize="small" />, bg: '#ffebee', accent: '#c62828' },
  cancelled: { color: 'default', icon: <WarningIcon fontSize="small" />, bg: '#eeeeee', accent: '#757575' },
  failed: { color: 'error', icon: <WarningIcon fontSize="small" />, bg: '#ffcdd2', accent: '#b71c1c' }
};

const SORT_OPTIONS = [
  { value: 'created_at|DESC', label: 'Plus récentes' },
  { value: 'created_at|ASC', label: 'Plus anciennes' },
  { value: 'total_amount|DESC', label: 'Montant décroissant' },
  { value: 'total_amount|ASC', label: 'Montant croissant' },
  { value: 'period_start|DESC', label: 'Période décroissante' },
  { value: 'due_date|ASC', label: 'Échéance proche' },
  { value: 'status|ASC', label: 'Statut A → Z' }
];

const STATUS_OPTIONS = ['all', 'draft', 'pending', 'link_sent', 'payment_initiated', 'paid', 'overdue', 'cancelled', 'failed'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatAmount = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(Number(amount) || 0);
  } catch {
    return `${Number(amount) || 0} ${currency}`;
  }
};

const getOwnerDisplayName = (owner = {}) => owner.name || [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.email || '—';

const StatCard = ({ title, value, helper, icon, accent = 'primary.main' }) => (
  <Card sx={{ height: '100%', borderRadius: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Avatar sx={{ bgcolor: 'transparent', color: accent }}>{icon}</Avatar>
      </Box>
      <Typography variant="h5" fontWeight={800} color={accent}>{value}</Typography>
      {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>}
    </CardContent>
  </Card>
);

const InvoiceCard = ({ invoice, onView, selected, onToggleSelect }) => {
  const cfg = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.pending;
  const canSend = ['draft', 'pending', 'link_sent', 'overdue'].includes(invoice.status);

  return (
    <Card sx={{ borderRadius: 3, height: '100%', border: `1px solid ${cfg.accent}25` }}>
      <Box sx={{ px: 2.5, py: 2, background: `linear-gradient(135deg, ${cfg.bg} 0%, #ffffff 100%)`, borderBottom: `2px solid ${cfg.accent}30` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Facture</Typography>
            <Typography variant="h6" fontWeight={800}>{invoice.invoice_number}</Typography>
            <Typography variant="body2" color="text.secondary">{getOwnerDisplayName(invoice.owner)}</Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={1}>
            <Checkbox
              size="small"
              checked={selected}
              disabled={!canSend}
              onChange={() => onToggleSelect(invoice.id)}
              inputProps={{ 'aria-label': `Sélectionner la facture ${invoice.invoice_number}` }}
            />
            <Chip icon={cfg.icon} label={invoice.status} color={cfg.color} size="small" sx={{ fontWeight: 700 }} />
          </Stack>
        </Box>
      </Box>

      <CardContent>
        <Typography variant="h5" fontWeight={900} color={cfg.accent} sx={{ mb: 1.5 }}>
          {formatAmount(invoice.total_amount, invoice.currency)}
        </Typography>

        <Stack spacing={1}>
          <Typography variant="body2"><strong>Période :</strong> {formatDate(invoice.period_start)} → {formatDate(invoice.period_end)}</Typography>
          <Typography variant="body2"><strong>Échéance :</strong> {formatDate(invoice.due_date)}</Typography>
          <Typography variant="body2"><strong>Créée le :</strong> {formatDate(invoice.created_at)}</Typography>
          <Typography variant="body2"><strong>Payins :</strong> {invoice.payins_count || 0}</Typography>
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<ReceiptIcon />} onClick={() => onView(invoice)}>
          Détails
        </Button>
      </CardActions>
    </Card>
  );
};

const AdminCommissionsTab = ({ onNotify }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 12 });
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    owner_id: '',
    period_start: '',
    period_end: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'DESC'
  });
  const [generateDialog, setGenerateDialog] = useState({ open: false, period: '', owner_ids: '' });

  const notify = (message, severity = 'info') => onNotify?.(message, severity);

  const loadInvoices = useCallback(async (page = pagination.page, currentFilters = filters) => {
    setLoading(true);
    try {
      const data = await adminCommissionService.getInvoices({
        page,
        limit: pagination.limit,
        ...currentFilters
      });
      setInvoices(data.invoices || []);
      setStats(data.stats || []);
      setPagination(prev => ({
        ...prev,
        page: data.pagination?.page || page,
        pages: data.pagination?.pages || 1,
        total: data.pagination?.total || 0,
        limit: data.pagination?.limit || prev.limit
      }));
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors du chargement des factures admin', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, onNotify, pagination.limit, pagination.page]);

  const loadCronStatus = useCallback(async () => {
    try {
      const data = await adminCommissionService.getCronStatus();
      setCronStatus(data);
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors du chargement du statut cron', 'error');
    }
  }, [onNotify]);

  const getOwnerOptionLabel = (owner) => {
    if (!owner) return '';
    const name = owner.name || [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.id || 'Utilisateur';
    return owner.email ? `${name} (${owner.email})` : name;
  };

  const loadOwners = useCallback(async (query = '') => {
    setLoadingOwners(true);
    try {
      const results = await adminCommissionService.searchOwners(query);
      setOwners(results);
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors du chargement des propriétaires', 'error');
    } finally {
      setLoadingOwners(false);
    }
  }, [onNotify]);

  const resetOwnerFilterState = useCallback(async () => {
    setSelectedOwner(null);
    setOwnerSearchInput('');
    setOwners([]);
    await loadOwners('');
  }, [loadOwners]);

  const loadAll = useCallback(async () => {
    await Promise.all([loadInvoices(1, filters), loadCronStatus(), loadOwners('')]);
  }, [filters, loadCronStatus, loadInvoices, loadOwners]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadOwners('');
  }, [loadOwners]);

  const statsByStatus = useMemo(() => {
    const index = {};
    stats.forEach((item) => { index[item.status] = item; });
    return index;
  }, [stats]);

  const handleSortChange = (value) => {
    const [sort_by, sort_order] = value.split('|');
    const nextFilters = { ...filters, sort_by, sort_order };
    setFilters(nextFilters);
    loadInvoices(1, nextFilters);
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
  };

  const applyFilters = () => loadInvoices(1, filters);

  const resetFilters = async () => {
    const nextFilters = {
      status: 'all',
      owner_id: '',
      period_start: '',
      period_end: '',
      search: '',
      sort_by: 'created_at',
      sort_order: 'DESC'
    };
    await resetOwnerFilterState();
    setSelectedInvoiceIds([]);
    setFilters(nextFilters);
    loadInvoices(1, nextFilters);
  };

  const handleToggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoiceIds((current) => (
      current.includes(invoiceId)
        ? current.filter((id) => id !== invoiceId)
        : [...current, invoiceId]
    ));
  };

  const sendableInvoicesOnPage = invoices.filter((invoice) => ['draft', 'pending', 'link_sent', 'overdue'].includes(invoice.status));
  const sendableInvoiceIdsOnPage = sendableInvoicesOnPage.map((invoice) => invoice.id);

  const handleToggleSelectAllCurrentPage = () => {
    const allSelected = sendableInvoiceIdsOnPage.length > 0 && sendableInvoiceIdsOnPage.every((id) => selectedInvoiceIds.includes(id));

    setSelectedInvoiceIds((current) => (
      allSelected
        ? current.filter((id) => !sendableInvoiceIdsOnPage.includes(id))
        : Array.from(new Set([...current, ...sendableInvoiceIdsOnPage]))
    ));
  };

  const visibleSelectedCount = sendableInvoicesOnPage.filter((invoice) => selectedInvoiceIds.includes(invoice.id)).length;
  const allCurrentPageSelected = sendableInvoiceIdsOnPage.length > 0 && sendableInvoiceIdsOnPage.every((id) => selectedInvoiceIds.includes(id));

  const openDetail = (invoice) => {
    navigate(`/admin/payins/commissions/${invoice.id}`);
  };

  const handleSendPaymentRequests = async (invoiceIds = []) => {
    const idsToSend = invoiceIds.length > 0 ? invoiceIds : selectedInvoiceIds;

    if (idsToSend.length === 0) {
      notify('Sélectionne au moins une facture à envoyer', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      const result = await adminCommissionService.sendPaymentRequests(idsToSend);
      const sent = result.paymentLinksSent ?? result.sent ?? 0;
      notify(`${sent} demande(s) de paiement envoyée(s)`, 'success');
      setSelectedInvoiceIds((current) => current.filter((id) => !idsToSend.includes(id)));
      await loadInvoices();
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors de l’envoi des demandes de paiement', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerate = async () => {
    setActionLoading(true);
    try {
      const payload = {};
      if (generateDialog.period.trim()) payload.period = generateDialog.period.trim();
      if (generateDialog.owner_ids.trim()) {
        payload.owner_ids = generateDialog.owner_ids.split(/[\s,;]+/).map(value => value.trim()).filter(Boolean);
      }
      await adminCommissionService.generate(payload);
      notify('Génération des commissions lancée', 'success');
      setGenerateDialog({ open: false, period: '', owner_ids: '' });
      await loadInvoices();
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors de la génération des commissions', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunCron = async () => {
    setActionLoading(true);
    try {
      await adminCommissionService.runCron();
      notify('Exécution manuelle du cron lancée', 'success');
      await Promise.all([loadInvoices(), loadCronStatus()]);
    } catch (error) {
      notify(error.response?.data?.error || 'Erreur lors de l’exécution manuelle du cron', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Factures payées"
            value={statsByStatus.paid?.count || 0}
            helper={formatAmount(statsByStatus.paid?.total_amount || 0, 'EUR')}
            icon={<CheckCircleIcon />}
            accent="success.main"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="En attente"
            value={(statsByStatus.pending?.count || 0) + (statsByStatus.link_sent?.count || 0)}
            helper="pending + link_sent"
            icon={<HourglassIcon />}
            accent="warning.main"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Overdue"
            value={statsByStatus.overdue?.count || 0}
            helper={formatAmount(statsByStatus.overdue?.total_amount || 0, 'EUR')}
            icon={<WarningIcon />}
            accent="error.main"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Cron commissions"
            value={cronStatus?.isRunning ? 'Actif' : 'Arrêté'}
            helper={cronStatus?.nextExecution ? `Prochaine exécution: ${cronStatus.nextExecution}` : 'Pas de prochaine exécution'}
            icon={<TrendingUpIcon />}
            accent={cronStatus?.isRunning ? 'success.main' : 'text.secondary'}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Commissions Admin</Typography>
            <Typography variant="body2" color="text.secondary">Factures, relances, génération manuelle et statut du cron</Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAll} disabled={loading || actionLoading}>Rafraîchir</Button>
            <Button variant="outlined" color="warning" startIcon={<SendIcon />} onClick={() => handleSendPaymentRequests()} disabled={actionLoading || selectedInvoiceIds.length === 0}>Envoyer la sélection ({selectedInvoiceIds.length})</Button>
            <Button variant="outlined" color="secondary" startIcon={<TrendingUpIcon />} onClick={handleRunCron} disabled={actionLoading}>Lancer le cron</Button>
            <Button variant="contained" startIcon={<ReceiptIcon />} onClick={() => setGenerateDialog({ open: true, period: '', owner_ids: '' })} disabled={actionLoading}>Générer</Button>
          </Stack>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Recherche facture" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Numéro de facture, note" />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select label="Statut" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>{status === 'all' ? 'Tous' : status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={owners}
              filterOptions={(options) => options}
              getOptionLabel={getOwnerOptionLabel}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedOwner}
              inputValue={ownerSearchInput}
              onChange={(_, value) => {
                setSelectedOwner(value || null);
                setOwnerSearchInput(value ? getOwnerOptionLabel(value) : '');
                handleFilterChange('owner_id', value?.id || '');
              }}
              onInputChange={(_, value, reason) => {
                setOwnerSearchInput(value);

                if (reason === 'clear') {
                  setSelectedOwner(null);
                  handleFilterChange('owner_id', '');
                  loadOwners('');
                  return;
                }

                if (reason !== 'input') {
                  return;
                }

                if (value.trim().length >= 2) {
                  loadOwners(value.trim());
                } else if (value.trim().length === 0) {
                  loadOwners('');
                }
              }}
              loading={loadingOwners}
              noOptionsText="Saisir un nom ou email"
              renderInput={(params) => (
                <TextField {...params} label="Propriétaire" placeholder="Chercher par nom/email" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Période début" InputLabelProps={{ shrink: true }} value={filters.period_start} onChange={(e) => handleFilterChange('period_start', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Période fin" InputLabelProps={{ shrink: true }} value={filters.period_end} onChange={(e) => handleFilterChange('period_end', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Trier par</InputLabel>
              <Select label="Trier par" value={`${filters.sort_by}|${filters.sort_order}`} onChange={(e) => handleSortChange(e.target.value)}>
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button fullWidth variant="contained" startIcon={<FilterListIcon />} onClick={applyFilters}>Appliquer</Button>
              <Button fullWidth variant="outlined" onClick={resetFilters}>Reset</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 3, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="text"
            startIcon={<Checkbox checked={allCurrentPageSelected} indeterminate={visibleSelectedCount > 0 && !allCurrentPageSelected} />}
            onClick={handleToggleSelectAllCurrentPage}
          >
            {allCurrentPageSelected ? 'Désélectionner la page' : 'Sélectionner la page'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {selectedInvoiceIds.length} facture(s) sélectionnée(s) au total, {visibleSelectedCount} visible(s) sur cette page
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="warning"
          startIcon={<SendIcon />}
          onClick={() => handleSendPaymentRequests()}
          disabled={actionLoading || selectedInvoiceIds.length === 0}
        >
          Envoyer les demandes sélectionnées
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : invoices.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>Aucune facture de commission trouvée avec ces filtres.</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {invoices.map((invoice) => (
              <Grid item xs={12} md={6} xl={4} key={invoice.id}>
                <InvoiceCard
                  invoice={invoice}
                  onView={openDetail}
                  selected={selectedInvoiceIds.includes(invoice.id)}
                  onToggleSelect={handleToggleInvoiceSelection}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pagination.pages || 1}
              page={pagination.page || 1}
              onChange={(_, page) => loadInvoices(page, filters)}
              color="primary"
            />
          </Box>
        </>
      )}

      <Dialog open={generateDialog.open} onClose={() => setGenerateDialog({ open: false, period: '', owner_ids: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Générer des commissions</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Période" placeholder="2026-03 ou libellé attendu par le backend" sx={{ mt: 1, mb: 2 }} value={generateDialog.period} onChange={(e) => setGenerateDialog(prev => ({ ...prev, period: e.target.value }))} />
          <TextField fullWidth multiline rows={4} label="Owner IDs" placeholder="UUID séparés par virgule, espace ou point-virgule" value={generateDialog.owner_ids} onChange={(e) => setGenerateDialog(prev => ({ ...prev, owner_ids: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog({ open: false, period: '', owner_ids: '' })}>Annuler</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={actionLoading}>Générer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCommissionsTab;